// File: src/components/AIProfilePersonalization.jsx
// Description: AI Profile Personalization component for testing and viewing personalized content

import React, { useState, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  useTheme
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import AIService from '../services/AIService';

const AIProfilePersonalization = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  
  const [viewerProfile, setViewerProfile] = useState({
    industry: 'tech',
    job_title: 'Software Engineer',
    company: 'TechCorp',
    interests: ['AI', 'Machine Learning'],
    location: 'San Francisco'
  });
  
  const [loading, setLoading] = useState(false);
  const [personalizedContent, setPersonalizedContent] = useState(null);
  const [error, setError] = useState(null);

  const handlePersonalize = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);
    setPersonalizedContent(null);

    try {
      console.log('Personalizing profile for viewer:', viewerProfile);
      const result = await AIService.getPersonalizedContent(viewerProfile, user);
      console.log('Personalization result:', result);
      setPersonalizedContent(result);
    } catch (err) {
      console.error('Profile personalization failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateViewerProfile = (field, value) => {
    setViewerProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateInterests = (interests) => {
    setViewerProfile(prev => ({
      ...prev,
      interests: interests.split(',').map(i => i.trim()).filter(i => i)
    }));
  };

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          🤖 AI Profile Personalization
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Test how your profile would be personalized for different viewers. The AI analyzes the viewer's profile 
          and generates customized content to make your profile more relevant and engaging.
        </Typography>

        {/* Viewer Profile Input */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Viewer Profile (Who's Viewing Your Profile)
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Industry"
                value={viewerProfile.industry}
                onChange={(e) => updateViewerProfile('industry', e.target.value)}
                placeholder="tech, finance, healthcare, etc."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={viewerProfile.job_title}
                onChange={(e) => updateViewerProfile('job_title', e.target.value)}
                placeholder="Software Engineer, Manager, etc."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={viewerProfile.company}
                onChange={(e) => updateViewerProfile('company', e.target.value)}
                placeholder="Company name"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={viewerProfile.location}
                onChange={(e) => updateViewerProfile('location', e.target.value)}
                placeholder="City, Country"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interests (comma-separated)"
                value={viewerProfile.interests.join(', ')}
                onChange={(e) => updateInterests(e.target.value)}
                placeholder="AI, Machine Learning, Innovation"
                size="small"
              />
            </Grid>
          </Grid>
        </Card>

        {/* Current User Profile Display */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Your Current Profile
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={user?.name || 'User'} color="primary" />
            <Chip label={user?.job_title || 'Professional'} color="secondary" />
            <Chip label={user?.company || 'Company'} variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {user?.bio || 'No bio available'}
          </Typography>
        </Card>

        {/* Personalize Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handlePersonalize}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ px: 4, py: 1.5 }}
          >
            {loading ? 'Personalizing...' : 'Generate Personalized Content'}
          </Button>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              AI is analyzing profiles and generating personalized content...
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Personalized Content Display */}
        {personalizedContent && (
          <Card variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.success.main }}>
              ✨ AI-Generated Personalized Content
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Personalized Bio */}
            {personalizedContent.personalized_bio && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Personalized Bio
                </Typography>
                <Typography variant="body1" sx={{ 
                  p: 2, 
                  bgcolor: theme.palette.background.default, 
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  {personalizedContent.personalized_bio}
                </Typography>
              </Box>
            )}

            {/* Priority Platforms */}
            {personalizedContent.priority_platforms && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Priority Platforms for This Viewer
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {personalizedContent.priority_platforms.map((platform, index) => (
                    <Chip 
                      key={index} 
                      label={platform} 
                      color="primary" 
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Content Adaptations */}
            {personalizedContent.content_adaptations && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Content Adaptations
                </Typography>
                <Grid container spacing={2}>
                  {personalizedContent.content_adaptations.bio && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Bio Focus
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {personalizedContent.content_adaptations.bio}
                      </Typography>
                    </Grid>
                  )}
                  {personalizedContent.content_adaptations.skills && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Highlighted Skills
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {personalizedContent.content_adaptations.skills.map((skill, index) => (
                          <Chip key={index} label={skill} size="small" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Personalization Score */}
            {personalizedContent.personalization_score && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Personalization Quality Score
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: theme.palette.success.main 
                }}>
                  {Math.round(personalizedContent.personalization_score * 100)}%
                </Typography>
              </Box>
            )}

            {/* Raw Response (for debugging) */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Raw AI Response (Debug)
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  maxHeight: 200,
                  overflow: 'auto'
                }}
              >
                {JSON.stringify(personalizedContent, null, 2)}
              </Box>
            </Box>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default AIProfilePersonalization;
